import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Clock, CheckCircle, AlertCircle,
  Loader2, Building2, Mail, Tag, Calendar,
  ThumbsUp, ThumbsDown, Star, Play, Send, XCircle
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: 'Pending', color: 'bg-violet-100 text-violet-700', icon: Clock },
  ai_evaluated: { label: 'AI Evaluated', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  reviewed: { label: 'Reviewed', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  valuable: { label: 'Valuable', color: 'bg-emerald-100 text-emerald-700', icon: ThumbsUp },
  declined: { label: 'Declined', color: 'bg-violet-100 text-violet-700', icon: XCircle },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

interface PitchDetailData {
  pitch: {
    product_name: string;
    vendor_name: string;
    category?: string;
    pitch_content: string;
    claims?: string[];
    status: string;
    created_at: string;
    source: string;
    vendor_email?: string;
  };
  evaluation?: {
    relevance_score?: number;
    legitimacy_score?: number;
    fit_score?: number;
    overall_score?: number;
    ai_recommendation?: string;
    ai_reasoning?: string;
    human_override_score?: number;
    human_notes?: string;
    final_recommendation?: string;
  };
}

export default function AdminPitchReviewPage() {
  const { pitchId } = useParams<{ pitchId: string }>();
  const { token } = useAuth();

  const [data, setData] = useState<PitchDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [markingValuable, setMarkingValuable] = useState(false);
  const [markingDeclined, setMarkingDeclined] = useState(false);

  const [reviewForm, setReviewForm] = useState({
    human_override_score: '',
    human_notes: '',
    final_recommendation: ''
  });

  useEffect(() => {
    fetchPitchDetails();
  }, [pitchId]);

  const fetchPitchDetails = async () => {
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load pitch details');
      }

      const result = await response.json();
      setData(result);

      if (result.evaluation) {
        setReviewForm({
          human_override_score: result.evaluation.human_override_score?.toString() || '',
          human_notes: result.evaluation.human_notes || '',
          final_recommendation: result.evaluation.final_recommendation || result.evaluation.ai_recommendation || ''
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const triggerAIEvaluation = async () => {
    setEvaluating(true);
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}/evaluate`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPitchDetails();
      } else {
        const error = await response.json();
        alert(error.detail || 'Evaluation failed');
      }
    } catch (err) {
      alert('Failed to trigger evaluation');
    } finally {
      setEvaluating(false);
    }
  };

  const submitReview = async () => {
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      if (reviewForm.human_override_score) {
        params.append('human_override_score', reviewForm.human_override_score);
      }
      if (reviewForm.human_notes) {
        params.append('human_notes', reviewForm.human_notes);
      }
      if (reviewForm.final_recommendation) {
        params.append('final_recommendation', reviewForm.final_recommendation);
      }

      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}/review?${params}`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPitchDetails();
      } else {
        const error = await response.json();
        alert(error.detail || 'Review submission failed');
      }
    } catch (err) {
      alert('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const deliverPitch = async () => {
    if (!confirm('Deliver this pitch to the merchant?')) return;

    setDelivering(true);
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}/deliver`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPitchDetails();
      } else {
        const error = await response.json();
        alert(error.detail || 'Delivery failed');
      }
    } catch (err) {
      alert('Failed to deliver pitch');
    } finally {
      setDelivering(false);
    }
  };

  const markValuable = async () => {
    if (!confirm('Mark this pitch as VALUABLE? It will appear in the merchant\'s recommendations.')) return;

    setMarkingValuable(true);
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}/mark-valuable`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPitchDetails();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to mark as valuable');
      }
    } catch (err) {
      alert('Failed to mark pitch as valuable');
    } finally {
      setMarkingValuable(false);
    }
  };

  const markDeclined = async () => {
    if (!confirm('Mark this pitch as DECLINED? It will NOT be shown to the merchant.')) return;

    setMarkingDeclined(true);
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}/mark-declined`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPitchDetails();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to mark as declined');
      }
    } catch (err) {
      alert('Failed to mark pitch as declined');
    } finally {
      setMarkingDeclined(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 text-violet-400 mx-auto mb-4" />
          <p className="text-stone-600">{error || 'Pitch not found'}</p>
        </div>
      </AdminLayout>
    );
  }

  const { pitch, evaluation } = data;
  const statusConfig = STATUS_CONFIG[pitch.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-pitch-review">
        {/* Back Link */}
        <Link
          to="/admin/pitches"
          className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all pitches
        </Link>

        {/* Header */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-stone-900">{pitch.product_name}</h1>
              <div className="flex items-center gap-4 mt-2 text-stone-600">
                <span className="inline-flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {pitch.vendor_name}
                </span>
                {pitch.category && (
                  <span className="inline-flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    {pitch.category}
                  </span>
                )}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-4 h-4" />
              {statusConfig.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Pitch Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pitch Content */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h2 className="text-lg font-semibold text-stone-900 mb-4">Pitch Content</h2>
              <p className="text-stone-700 whitespace-pre-wrap">{pitch.pitch_content}</p>

              {pitch.claims && pitch.claims.length > 0 && (
                <div className="mt-6 pt-6 border-t border-stone-100">
                  <p className="text-sm font-medium text-stone-700 mb-3">Claims</p>
                  <div className="flex flex-wrap gap-2">
                    {pitch.claims.map((claim, i) => (
                      <span key={i} className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-sm">
                        {claim}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* AI Evaluation */}
            {evaluation && (
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-900 mb-4">AI Evaluation</h2>

                {/* Scores Grid */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">{evaluation.relevance_score}</p>
                    <p className="text-xs text-violet-600">Relevance</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">{evaluation.legitimacy_score}</p>
                    <p className="text-xs text-purple-600">Legitimacy</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700">{evaluation.fit_score}</p>
                    <p className="text-xs text-emerald-600">Fit</p>
                  </div>
                  <div className="text-center p-4 bg-stone-100 rounded-lg">
                    <p className="text-2xl font-bold text-stone-900">{evaluation.overall_score}</p>
                    <p className="text-xs text-stone-600">Overall</p>
                  </div>
                </div>

                {/* AI Recommendation */}
                <div className="mb-6">
                  <p className="text-sm text-stone-500 mb-2">AI Recommendation</p>
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
                    evaluation.ai_recommendation === 'pursue' ? 'bg-emerald-100 text-emerald-700' :
                    evaluation.ai_recommendation === 'maybe' ? 'bg-violet-100 text-violet-700' :
                    'bg-violet-100 text-violet-700'
                  }`}>
                    {evaluation.ai_recommendation === 'pursue' ? <ThumbsUp className="w-4 h-4" /> :
                      evaluation.ai_recommendation === 'maybe' ? <Star className="w-4 h-4" /> :
                        <ThumbsDown className="w-4 h-4" />}
                    {evaluation.ai_recommendation?.toUpperCase()}
                  </span>
                </div>

                {/* AI Reasoning */}
                {evaluation.ai_reasoning && (
                  <div>
                    <p className="text-sm text-stone-500 mb-2">AI Analysis</p>
                    <p className="text-stone-600 text-sm whitespace-pre-wrap bg-stone-50 p-4 rounded-lg">
                      {evaluation.ai_reasoning}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Actions & Review */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="text-lg font-semibold text-stone-900 mb-4">Actions</h3>

              {pitch.status === 'pending' && (
                <Button
                  onClick={triggerAIEvaluation}
                  disabled={evaluating}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white mb-3"
                >
                  {evaluating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Run AI Evaluation
                </Button>
              )}

              {pitch.status === 'reviewed' && (
                <div className="space-y-3">
                  <Button
                    onClick={markValuable}
                    disabled={markingValuable}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {markingValuable ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ThumbsUp className="w-4 h-4 mr-2" />
                    )}
                    Mark as Valuable
                  </Button>
                  <Button
                    onClick={markDeclined}
                    disabled={markingDeclined}
                    variant="outline"
                    className="w-full border-red-300 text-violet-600 hover:bg-violet-50"
                  >
                    {markingDeclined ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-2" />
                    )}
                    Mark as Declined
                  </Button>
                </div>
              )}

              {pitch.status === 'valuable' && (
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <ThumbsUp className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-emerald-700 font-medium">Marked as Valuable</p>
                  <p className="text-emerald-600 text-sm mt-1">Visible in merchant recommendations</p>
                </div>
              )}

              {pitch.status === 'declined' && (
                <div className="text-center p-4 bg-violet-50 rounded-lg">
                  <XCircle className="w-8 h-8 text-violet-600 mx-auto mb-2" />
                  <p className="text-violet-700 font-medium">Declined</p>
                  <p className="text-violet-600 text-sm mt-1">Not shown to merchant</p>
                </div>
              )}

              {pitch.status === 'delivered' && (
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-emerald-700 font-medium">Delivered</p>
                </div>
              )}
            </div>

            {/* Human Review Form */}
            {evaluation && pitch.status !== 'pending' && (
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-4">Human Review</h3>

                <div className="space-y-4">
                  <div>
                    <Label className="text-stone-700">Override Score (0-100)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={reviewForm.human_override_score}
                      onChange={(e) => setReviewForm({ ...reviewForm, human_override_score: e.target.value })}
                      placeholder={evaluation.overall_score?.toString()}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-stone-700">Final Recommendation</Label>
                    <div className="flex gap-2 mt-2">
                      {['pursue', 'maybe', 'skip'].map((rec) => (
                        <button
                          key={rec}
                          onClick={() => setReviewForm({ ...reviewForm, final_recommendation: rec })}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            reviewForm.final_recommendation === rec
                              ? rec === 'pursue' ? 'bg-emerald-600 text-white' :
                                rec === 'maybe' ? 'bg-violet-600 text-white' :
                                  'bg-violet-600 text-white'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          {rec.charAt(0).toUpperCase() + rec.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-stone-700">Notes</Label>
                    <Textarea
                      value={reviewForm.human_notes}
                      onChange={(e) => setReviewForm({ ...reviewForm, human_notes: e.target.value })}
                      placeholder="Add any notes or context..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    onClick={submitReview}
                    disabled={submitting}
                    className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Submit Review
                  </Button>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="text-sm font-medium text-stone-500 mb-4">Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-500">Created</span>
                  <span className="text-stone-900">{formatDate(pitch.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Source</span>
                  <span className="text-stone-900 capitalize">{pitch.source}</span>
                </div>
                {pitch.vendor_email && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">Email</span>
                    <span className="text-stone-900">{pitch.vendor_email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
